import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, ErrorNotice, Field } from '@bcecloud/portal-ui';
import { AuthCard } from '@/components/AuthCard';
import { PasswordInput } from '@/components/PasswordInput';
import { resetPassword } from '@/lib/auth';

/**
 * Reset password page — accepts a token from the URL and a new password.
 */
export function ResetPasswordPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('reset_password_token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  /**
   * Sets the new password.
   *
   * @param event - The submit event.
   */
  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch {
      setError('Reset link is invalid or has expired. Please request a new one.');
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthCard title="Invalid link" subtitle="This reset link is invalid. Please request a new one.">
        <p className="text-center text-portal-small">
          <Link
            to="/auth/forgot-password"
            className="font-bold text-brand-link underline-offset-2 hover:underline"
          >
            Request a new reset link
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={success ? 'Password reset' : 'Set new password'}
      subtitle={
        success
          ? 'Your password has been changed successfully.'
          : 'Enter your new password below.'
      }
    >
      {success ? (
        <div className="text-center">
          <Button variant="primary" href="/auth/login">
            Sign in
          </Button>
        </div>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4" noValidate>
          {error ? <ErrorNotice inline message={error} /> : null}

          <Field label="New password" htmlFor="password">
            <PasswordInput
              id="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Field label="Confirm password" htmlFor="confirmPassword">
            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </Field>

          <Button type="submit" variant="primary" disabled={loading} className="w-full">
            {loading ? 'Resetting…' : 'Reset password'}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
